// ~/apps/themusicchurch-app/frontend/src/components/Layout/Header.jsx

import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components";

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${(props) => props.theme.ministryPage ? "#071c2df5" : "rgba(255, 255, 255, 0.95)"};
  backdrop-filter: blur(10px);
  a:focus-visible, button:focus-visible { outline: 2px solid ${(props) => props.theme.colors.accent}; outline-offset: 4px; }
  box-shadow: ${(props) => (props.scrolled ? props.theme.shadows.md : "none")};
  z-index: 1000;
  transition: all ${(props) => props.theme.transitions.base};
`;

const HeaderContainer = styled.div`
  max-width: ${(props) => props.theme.breakpoints.wide};
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.md}
    ${(props) => props.theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-family: "DIN Condensed", "Arial Narrow", sans-serif;
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: .04em;
  color: ${(props) => (props.theme.ministryPage ? props.theme.colors.homeGold : props.theme.colors.primary)};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};

  &:hover {
    color: ${(props) => (props.theme.ministryPage ? props.theme.colors.accent : props.theme.colors.secondary)};
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;

  @media (width < ${(props) => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  gap: ${(props) => props.theme.spacing.xl};
  align-items: center;
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled(Link)`
  font-family: ${(props) => props.theme.fonts.body};
  font-size: ${(props) => props.theme.fontSizes.base};
  font-weight: 500;
  color: ${(props) =>
    props.active ? (props.theme.ministryPage ? props.theme.colors.homeGold : props.theme.colors.primary) : (props.theme.ministryPage ? props.theme.colors.homeText : props.theme.colors.gray[700])};
  text-decoration: none;
  transition: color ${(props) => props.theme.transitions.fast};
  padding: ${(props) => props.theme.spacing.sm} 0;

  &:hover {
    color: ${(props) => (props.theme.ministryPage ? props.theme.colors.homeGold : props.theme.colors.primary)};
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    width: ${(props) => (props.active ? "100%" : "0")};
    height: 2px;
    background: ${(props) => (props.theme.ministryPage ? props.theme.colors.homeGold : props.theme.colors.primary)};
    transition: width ${(props) => props.theme.transitions.base};
  }

  &:hover::after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${(props) => props.theme.spacing.sm};

  @media (width < ${(props) => props.theme.breakpoints.tablet}) {
    display: block;
  }

  img {
    width: 30px;
    height: 30px;
    transition: transform ${(props) => props.theme.transitions.fast};
  }

  &:hover img {
    transform: scale(1.1);
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 400px;
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: ${(props) => props.theme.colors.homeNavy};
  color: ${(props) => props.theme.colors.homeText};
  visibility: ${(props) => props.$open ? "visible" : "hidden"};
  pointer-events: ${(props) => props.$open ? "auto" : "none"};
  a:focus-visible, button:focus-visible { outline: 2px solid ${(props) => props.theme.colors.accent}; outline-offset: 4px; }
  box-shadow: ${(props) => props.theme.shadows.xl};
  transform: translateX(${(props) => (props.$open ? "0" : "100%")});
  transition: transform ${(props) => props.theme.transitions.base};
  z-index: 2000;
  padding: ${(props) => props.theme.spacing["2xl"]};

  @media (min-width: ${(props) => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing["2xl"]};
`;

const MobileMenuTitle = styled.h3`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes.xl};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${(props) => props.theme.spacing.sm};

  img {
    width: 24px;
    height: 24px;
  }
`;

const MobileNavList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing.lg};
`;

const MobileNavLink = styled(Link)`
  display: block;
  min-height: 44px;
  font-family: ${(props) => props.theme.fonts.body};
  font-size: ${(props) => props.theme.fontSizes.lg};
  font-weight: 500;
  color: ${(props) =>
    props.active ? props.theme.colors.homeGold : props.theme.colors.homeText};
  text-decoration: none;
  padding: ${(props) => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.homeSurface};

  &:hover {
    color: ${(props) => props.theme.colors.homeGold};
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.$open ? "block" : "none")};
  z-index: 1500;
  @media (min-width: ${(props) => props.theme.breakpoints.tablet}) { display: none; }
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    const desktop = window.matchMedia(`(min-width: ${theme.breakpoints.tablet})`);
    const closeOnDesktop = () => { if (desktop.matches) setMobileMenuOpen(false); };
    closeOnDesktop();
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, [theme.breakpoints.tablet]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const body = document.body;
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const previous = Object.fromEntries(["position", "top", "left", "width", "overflow"].map(key => [key, body.style[key]]));
    Object.assign(body.style, { position: "fixed", top: `-${scrollY}px`, left: `-${scrollX}px`, width: "100%", overflow: "hidden" });
    const toggle = toggleRef.current;
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); setMobileMenuOpen(false); }
      if (event.key !== "Tab") return;
      const items = menuRef.current?.querySelectorAll('a[href], button:not([disabled])');
      if (!items?.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      Object.assign(body.style, previous);
      window.scrollTo(scrollX, scrollY);
      root.style.scrollBehavior = previousScrollBehavior;
      if (toggle?.isConnected && toggle.getClientRects().length) toggle.focus({ preventScroll: true });
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/ministries", label: "Ministries" },
    { path: "/events", label: "Events" },
    { path: "/books", label: "Books" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <>
      <HeaderWrapper scrolled={scrolled}>
        <HeaderContainer>
          <Logo to="/">THE MUSIC CHURCH</Logo>

          <Nav>
            <NavList>
              {navItems.map((item) => (
                <NavItem key={item.path}>
                  <NavLink
                    to={item.path}
                    active={
                      location.pathname === item.path ||
                      (item.path === "/ministries" &&
                        location.pathname.startsWith("/ministries"))
                    }
                  >
                    {item.label}
                  </NavLink>
                </NavItem>
              ))}
            </NavList>
          </Nav>

          <MobileMenuButton
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            ref={toggleRef}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            aria-label="Toggle mobile menu"
          >
            <img src="/images/hamburger-menu-icon-001.png" alt="Menu" />
          </MobileMenuButton>
        </HeaderContainer>
      </HeaderWrapper>

      <Overlay aria-hidden="true" $open={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />

      <MobileMenu id="mobile-navigation" ref={menuRef} $open={mobileMenuOpen} role="dialog" aria-modal={mobileMenuOpen ? "true" : undefined} aria-labelledby="mobile-menu-title" aria-hidden={!mobileMenuOpen} inert={mobileMenuOpen ? undefined : ""}>
        <MobileMenuHeader>
          <MobileMenuTitle id="mobile-menu-title">Menu</MobileMenuTitle>
          <CloseButton
            onClick={() => setMobileMenuOpen(false)}
            ref={closeRef}
            aria-label="Close menu"
          >
            <img src="/images/close-icon-001.png" alt="Close" />
          </CloseButton>
        </MobileMenuHeader>

        <MobileNavList>
          {navItems.map((item) => (
            <li key={item.path}>
              <MobileNavLink
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                active={
                  location.pathname === item.path ||
                  (item.path === "/ministries" &&
                    location.pathname.startsWith("/ministries"))
                }
              >
                {item.label}
              </MobileNavLink>
            </li>
          ))}
        </MobileNavList>
      </MobileMenu>
    </>
  );
};

export default Header;
